import { useSelector, useDispatch } from "react-redux";
import Table from "@/components/Table/table";
import { useColumnDefs } from "@/features/claims/claimsTab/Constant/claimHeaderConst";
import TitleComplement from "@/features/claims/claimsTab/components/titleComplement";
import {
  useGetCaseClaimListDataQuery,
  useLazyGetClaimListByCaseQuickSearchQuery,
  useLazyGetClaimBasicSearchQuery,
} from "@/services/cases/casesReducer";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { CaseRenderers } from "@/features/claims/claimsTab/Constant/customCellRenderer";
import BasicSearch from "../../claims/claimsTab/components/basicSearch";
import { useCallback, useEffect, useState } from "react";
import { useCase } from "@/features/case/use-case";
import { setShowBasicSeach } from "../claimsSlice";
import AdvanceSearch from "./components/AdvanceSearch";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useGetClaimAdvanceSearchDataMutation } from "@/services/advanceSearch/advanceSearchReducer";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  cleanPayload,
  commonFormateName,
  havingEditPermission,
} from "@/utility/common";
import { useTranslation } from "react-i18next";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import "./style.scss";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import ClaimBulkImport from "./components/ClaimBulkImport";
import { useLazyDownloadTemplateDocumentQuery } from "@/services/manualDataSource/ManualDataSourceReducer";
import BulkStageDocumentAttachment from "./components/BulkStageDocumentAttachment";

export default function ClaimsTabContent() {
  const caseData = useCase();
  const { caseId } = caseData;
  const dispatch = useDispatch();
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const retainState = useSelector((e: any) => e.retainState.claimListTabState);
  const [showUpdateStatusMessage, setShowUpdateStatusMessage] = useState({
    message: "",
    show: false,
    type: "",
  });
  const [paginationObj, setPaginationObj] = useState<any>(() => {
    let value: any = {
      CaseId: caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  });

  function handlePaginationData(data: any, columnTypesData: any) {
    setPaginationObj((prev: any) => {
      return {
        ...prev,
        SortOrder: data.SortOrder,
        SortColumn: data.SortColumn,
      };
    });
    dispatch(
      setComponentState1({
        entityName: "claimListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
    setBasicSearchData([]);
    setAdvanceSearchData([]);
  }
  let resetSortStatus = useCallback(
    (defaultSort: string) => {
      let json: any = { ...retainState.sortedColumnTypes };
      for (const key in json) {
        json[key] = { sort: null };
      }
      json[defaultSort] = { sort: "desc" };
      return json;
    },
    [retainState]
  );
  const permissions = useSelector((state: any) => state.permissions);
  const { Claim, ImportJob } = permissions;
  let writeAcess = havingEditPermission(Claim);
  let columnDefs = useColumnDefs(caseData, writeAcess);
  let caseRenderers = CaseRenderers();
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText);
  const [isSearchActive, setIsSearchActive] = useState(
    retainState.isSearchActive
  );
  const [isBasicSearchActive, setIsBasicSearchActive] = useState(
    retainState.isBasicSearchActive
  );
  const [basicSearchData, setBasicSearchData] = useState<any>();
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [isAdvanceSearchActive, setIsAdvanceSearchActive] = useState(
    retainState.isAdvanceSearchActive
  );
  const [advanceSearchData, setAdvanceSearchData] = useState<any>(null);
  const [advanceSearchPayload, setAdvanceSearchPayload] = useState(
    retainState.advanceSearchPayload
  );
  const [basicSearchPayload, setBasicSearchPayload] = useState(
    retainState.basicSearchPayload
  );
  const [showBulkStageAttachmentModal, setShowBulkStageAttachmentModal] =
    useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkImportType, setBulkImportType] = useState<"stage" | "mapping">(
    "stage"
  );
  const [downloadDocument, { isLoading: templetDowmloadLoading }] =
    useLazyDownloadTemplateDocumentQuery();
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );
  const [templateDownloadError, setTemplateDownloadError] = useState<
    string | undefined
  >("");

  const handleClose = () => {
    setShowUpdateStatusMessage({
      message: "",
      show: false,
      type: "",
    });
    setShowBulkUploadModal(false);
    templateDownloadError && setTemplateDownloadError("");
  };
  const handleDownloadBulkImportTemplate = async (entityType?: string) => {
    try {
      await downloadDocument({
        url: !["7", "11"].includes(entityType || "")
          ? "Claim/DownloadClaimTemplate"
          : "",
        caseId,
        entityType: entityType || "2",
        fileName: caseShortName
          ? commonFormateName(
              caseShortName,
              entityType === "7"
                ? "StageDocUploadMappingTemplate"
                : entityType === "11"
                ? "SupportingDocUploadMappingTemplate"
                : "ClaimImportTemplate",
              abbreviation
            )
          : "",
      }).unwrap();
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "data" in error &&
        error?.data
      ) {
        const errObj = JSON.parse((error as any).data);
        return setTemplateDownloadError(errObj?.message || errObj?.title);
      } else setTemplateDownloadError("Something went wrong!!");
    }
  };
  const { data, error, isLoading, isFetching, refetch, status } =
    useGetCaseClaimListDataQuery(paginationObj, {
      skip: isBasicSearchActive || isAdvanceSearchActive || isSearchActive,
    });
  const { t } = useTranslation();
  const [selectedClaimIds, setSelectedClaimIds] = useState<
    {
      claimReferenceId: string;
    }[]
  >([]);
  const [allClaimsSelected, setAllClaimsSelected] = useState(false);

  const clearSelectedClaimIds = (closeTrigger: boolean) => {
    setSelectedClaimIds([]);
    if (closeTrigger) {
      setPaginationObj({
        CaseId: caseId,
        PageNumber: 1,
        PageSize: paginationPageSize,
        SearchText: paginationObj.SearchText,
        SortOrder: "desc",
        SortColumn: "UpdatedOn",
      });
      setBasicSearchData([]);
      setAdvanceSearchData([]);
      dispatch(
        setComponentState1({
          entityName: "claimListTabState",
          entityState: {
            PageNumber: 1,
            PageSize: paginationPageSize,
            SearchText: paginationObj.SearchText,
            SortOrder: "desc",
            SortColumn: "UpdatedOn",
            sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
          },
        })
      );
    }
  };

  const [
    triggerSearch,
    {
      data: searchClaimDataByCase,
      isFetching: isSearchClaimDataByCaseFetching,
      error: isSearchClaimDataByCaseerror,
      status: searchDataStatus,
    },
  ] = useLazyGetClaimListByCaseQuickSearchQuery();
  const [
    getClaimAdvanceSearchData,
    {
      data: advanceTypeSearchData,
      isLoading: advanceLoading,
      error: advanceSearchError,
      status: advanceSearchStatus,
    },
  ] = useGetClaimAdvanceSearchDataMutation();

  const [
    triggerBasicSearch,
    {
      data: searchBasicData,
      isFetching: basicSearchFetching,
      error: basicSearchError,
      status: basicSearchStatus,
    },
  ] = useLazyGetClaimBasicSearchQuery();

  const showBasicSearch = useSelector(
    (state: any) => state.claims.showBasicSearch
  );
  const headerData = {
    title: "pages.claims.claimsTable.title",
    description: "pages.claims.claimsTable.description",
  };
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchQuery(searchValue);
      setIsBasicSearchActive(false);
      setIsAdvanceSearchActive(false);
      setAdvanceSearchData(null);
      setBasicSearchPayload({});
      setAdvanceSearchPayload({});
      if (searchValue.trim() === "") {
        setPaginationObj({
          CaseId: caseId,
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
        });
        dispatch(
          setComponentState1({
            entityName: "claimListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: "",
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              isAdvanceSearchActive: false,
              isBasicSearchActive: false,
              isSearchActive: false,
              advanceSearchPayload: {},
              basicSearchPayload: {},
              sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
            },
          })
        );

        setIsSearchActive(false);
      } else {
        setPaginationObj({
          CaseId: caseId,
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: searchValue,
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
        });
        dispatch(
          setComponentState1({
            entityName: "claimListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: searchValue,
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              isAdvanceSearchActive: false,
              isBasicSearchActive: false,
              isSearchActive: true,
              advanceSearchPayload: {},
              basicSearchPayload: {},
              sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
            },
          })
        );
        setIsSearchActive(true);
      }
    },
    [caseId, paginationPageSize, resetSortStatus, dispatch]
  );

  useEffect(() => {
    setSelectedClaimIds([]);
    let pageData = { ...paginationObj };
    delete pageData?.CaseId;
    if (isBasicSearchActive) {
      const cleanedBasicSearchPayload = cleanPayload(basicSearchPayload);
      triggerBasicSearch({ ...cleanedBasicSearchPayload, ...pageData })
        .then((response) => {
          // @ts-ignore
          const newData = response?.data?.results.map((item: any) => {
            return {
              claimReferenceId: item.claimReference,
              claimStageName: item.claimStage,
              actionStatusName: item.claimStatus,
              ...item,
            };
          });
          // @ts-ignore
          setBasicSearchData(newData);
        })
        .catch((error) => {
          console.error("Basic Search Failed", error);
        });
    } else if (isAdvanceSearchActive) {
      getClaimAdvanceSearchData({ ...advanceSearchPayload, ...pageData }).then(
        (response: any) => {
          if (response.data) {
            setAdvanceSearchData(response.data?.results);
          } else {
          }
        }
      );
    } else if (isSearchActive) {
      triggerSearch({
        ...paginationObj,
      })
        .unwrap()
        .catch((error) => {
          console.error("Search failed:", error);
        });
    } else {
      refetch();
    }
  }, [
    paginationObj,
    refetch,
    isAdvanceSearchActive,
    isSearchActive,
    advanceSearchPayload,
    basicSearchPayload,
    isBasicSearchActive,
    triggerBasicSearch,
    triggerSearch,
    getClaimAdvanceSearchData,
  ]);
  useEffect(() => {
    return () => {
      dispatch(setShowBasicSeach(false));
    };
  }, [dispatch]);
  const handleBasicSearch = useCallback(
    (searchParams: any) => {
      setShowUpdateStatusMessage({
        message: "",
        show: false,
        type: "",
      });
      setPaginationObj({
        CaseId: caseId,
        PageNumber: 1,
        PageSize: paginationPageSize,
        SearchText: "",
        SortOrder: "desc",
        SortColumn: "UpdatedOn",
      });
      dispatch(
        setComponentState1({
          entityName: "claimListTabState",
          entityState: {
            PageNumber: 1,
            PageSize: paginationPageSize,
            SearchText: "",
            SortOrder: "desc",
            SortColumn: "UpdatedOn",
            isBasicSearchActive: true,
            basicSearchPayload: searchParams,
            isSearchActive: false,
            sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
          },
        })
      );
      setBasicSearchData([]);
      setAdvanceSearchData([]);
      setSearchQuery("");
      setBasicSearchPayload(searchParams);
      setIsSearchActive(false);
      setIsBasicSearchActive(true);
    },
    [
      caseId,
      paginationPageSize,
      resetSortStatus,
      dispatch,
      setShowUpdateStatusMessage,
    ]
  );

  const handleQuickSearchClear = useCallback(() => {
    setShowUpdateStatusMessage({
      message: "",
      show: false,
      type: "",
    });
    setSearchQuery("");
    setIsSearchActive(false);
    setIsBasicSearchActive(false);
    setIsAdvanceSearchActive(false);
    setBasicSearchPayload({});
    setAdvanceSearchPayload({});
    setPaginationObj({
      CaseId: caseId,
      PageNumber: 1,
      PageSize: paginationPageSize,
      SearchText: "",
      SortOrder: "desc",
      SortColumn: "UpdatedOn",
    });
    dispatch(
      setComponentState1({
        entityName: "claimListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          isAdvanceSearchActive: false,
          isBasicSearchActive: false,
          isSearchActive: false,
          advanceSearchPayload: {},
          basicSearchPayload: {},
          sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
        },
      })
    );
  }, [caseId, paginationPageSize, resetSortStatus, dispatch]);
  const handleBasicSearchClear = useCallback(() => {
    setShowUpdateStatusMessage({
      message: "",
      show: false,
      type: "",
    });
    setIsBasicSearchActive(false);
    setIsAdvanceSearchActive(false);
    setAdvanceSearchData(null);
    setAdvanceSearchPayload({});
    setBasicSearchPayload({});
    setShowAdvanceSearch(false);
    dispatch(setShowBasicSeach(false));
    setPaginationObj({
      CaseId: caseId,
      PageNumber: 1,
      PageSize: paginationPageSize,
      SearchText: "",
      SortOrder: "desc",
      SortColumn: "UpdatedOn",
    });
    dispatch(
      setComponentState1({
        entityName: "claimListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          isAdvanceSearchActive: false,
          isBasicSearchActive: false,
          advanceSearchPayload: {},
          basicSearchPayload: {},
          sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
        },
      })
    );
  }, [caseId, paginationPageSize, resetSortStatus, dispatch]);
  function handleReset(advanceSearchPayload: any) {
    setShowUpdateStatusMessage({
      message: "",
      show: false,
      type: "",
    });
    setPaginationObj({
      CaseId: caseId,
      PageNumber: 1,
      PageSize: paginationPageSize,
      SearchText: "",
      SortOrder: "desc",
      SortColumn: "UpdatedOn",
    });
    dispatch(
      setComponentState1({
        entityName: "claimListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          isAdvanceSearchActive: true,
          isBasicSearchActive: false,
          advanceSearchPayload: advanceSearchPayload,
          sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
        },
      })
    );
    setSearchQuery("");
    setIsBasicSearchActive(false);
    setIsSearchActive(false);
    setBasicSearchData([]);
    setAdvanceSearchData([]);
  }
  function refreshApiOnBulkClaimCreateSuccess() {
    setPaginationObj({
      CaseId: caseId,
      PageNumber: 1,
      PageSize: paginationPageSize,
      SearchText: "",
      SortOrder: "desc",
      SortColumn: "UpdatedOn",
    });
    dispatch(
      setComponentState1({
        entityName: "claimListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          isAdvanceSearchActive: false,
          isBasicSearchActive: false,
          isSearchActive: false,
          sortedColumnTypes: { ...resetSortStatus("lastUpdatedDateType") },
        },
      })
    );
    setSearchQuery("");
    setIsBasicSearchActive(false);
    setIsSearchActive(false);
    setIsAdvanceSearchActive(false);
  }

  const tableDataFetching =
    advanceLoading ||
    basicSearchFetching ||
    isFetching ||
    isSearchClaimDataByCaseFetching;
  const tableDataError =
    advanceSearchError ||
    basicSearchError ||
    error ||
    isSearchClaimDataByCaseerror;
  const tableDataHasRecords = isAdvanceSearchActive
    ? advanceTypeSearchData?.hasMoreRecords
    : isSearchActive
    ? searchClaimDataByCase?.hasMoreRecords
    : isBasicSearchActive
    ? searchBasicData?.hasMoreRecords
    : data?.hasMoreRecords;
  const handleRowSelected = (event: any) => {
    const selectedNode = event.node;
    const selectedData = selectedNode.data;
    const isSelected = selectedNode.isSelected();

    setSelectedClaimIds((prevSelectedClaimIds) =>
      isSelected
        ? [...prevSelectedClaimIds, selectedData]
        : prevSelectedClaimIds.filter(
            (item) => item.claimReferenceId !== selectedData.claimReferenceId
          )
    );
  };
  if (isLoading) {
    return <MotifProgressLoader show variant="default" />;
  }

  const handleAllClaimsSelected = (isSelected: boolean) => {
    setAllClaimsSelected(isSelected);
  };

  const onSelectionChanged = (event: any) => {
    const allSelected =
      event.api.getSelectedNodes().length === event.api.getDisplayedRowCount();
    handleAllClaimsSelected(allSelected);
  };

  const handleEditSearch = () => {
    if (isAdvanceSearchActive) {
      dispatch(setShowBasicSeach(false));
      setShowAdvanceSearch(true);
    } else if (isBasicSearchActive) {
      dispatch(setShowBasicSeach(true));
      setShowAdvanceSearch(false);
    }
  };

  const tableData = isAdvanceSearchActive
    ? advanceSearchData
    : isSearchActive
    ? searchClaimDataByCase?.results
    : isBasicSearchActive
    ? basicSearchData
    : data?.results;
  const tableDataOgj = isAdvanceSearchActive
    ? advanceTypeSearchData
    : isSearchActive
    ? searchClaimDataByCase
    : isBasicSearchActive
    ? searchBasicData
    : data;
  const tableDataOgjStatus = isAdvanceSearchActive
    ? advanceSearchStatus
    : isSearchActive
    ? searchDataStatus
    : isBasicSearchActive
    ? basicSearchStatus
    : status;

  return (
    <>
      <div
        className={
          "ccwp_claim_claimlisttable " +
          (showBasicSearch || showAdvanceSearch ? "hide_claims_table" : "")
        }
      >
        {errorMsg && (
          <NotificationGrid show={true} type="error" message={errorMsg} />
        )}
        {showUpdateStatusMessage.show && (
          <>
            <NotificationGrid
              show={showUpdateStatusMessage.show}
              type={showUpdateStatusMessage.type}
              message={showUpdateStatusMessage.message}
              scrollToTop={false}
              onClose={() =>
                setShowUpdateStatusMessage({
                  message: "",
                  show: false,
                  type: "",
                })
              }
            />
            <br />
          </>
        )}
        <>
          <Table
            columnDefs={columnDefs}
            columnTypes={{ ...retainState.sortedColumnTypes }}
            handlePaginationData={handlePaginationData}
            paginationObj={paginationObj}
            cellRenderers={tableDataFetching ? [] : tableData ? tableData : []}
            TitleComplement={() => (
              <TitleComplement
                caseId={caseId || caseData.caseId}
                onSearch={handleSearch}
                handleQuickSearchClear={handleQuickSearchClear}
                searchQuery={searchQuery}
                isBasicSearch={isBasicSearchActive}
                handleClearBasicSearch={handleBasicSearchClear}
                setShowAdvanceSearch={setShowAdvanceSearch}
                isAdvanceSearch={isAdvanceSearchActive}
                showAdvanceSearch={showAdvanceSearch}
                showBasicSearch={showBasicSearch}
                advanceSearchPayload={advanceSearchPayload}
                basicSearchPayload={basicSearchPayload}
                handleEditSearch={handleEditSearch}
                selectedClaimIds={selectedClaimIds}
                clearSelectedClaimIds={clearSelectedClaimIds}
                allClaimsSelected={allClaimsSelected}
                isAdvanceSearchActive={isAdvanceSearchActive}
                isBasicSearchActive={isBasicSearchActive}
                isSearchActive={isSearchActive}
                paginationObj={paginationObj}
                Claim={Claim}
                ImportJob={ImportJob}
                onBulkClaimCreateSuccess={refreshApiOnBulkClaimCreateSuccess}
                setErrorMessage={setErrorMsg}
                setShowBulkUploadModal={setShowBulkUploadModal}
                setShowBulkStageAttachmentModal={
                  setShowBulkStageAttachmentModal
                }
                setShowUpdateStatusMessage={setShowUpdateStatusMessage}
                setBulkImportType={setBulkImportType}
                setTemplateDownloadError={setTemplateDownloadError}
              />
            )}
            title={headerData.title}
            description={headerData.description}
            caseRenderers={caseRenderers}
            onRowSelected={handleRowSelected}
            rowSelection="multiple"
            pagination={false}
            overlayNoRowsTemplate={
              tableDataFetching
                ? t(tableLoadingData)
                : tableDataError
                ? t("global.globalNotifications.wentWrong")
                : t("global.globalNotifications.noRecords")
            }
            onSelectionChanged={onSelectionChanged}
          />
          {!showBasicSearch && !showAdvanceSearch && (
            <CustomPagination
              apiData={tableDataOgj}
              apiStatus={tableDataOgjStatus}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!tableDataHasRecords}
              onChangePageSize={(page: number) => {
                setPaginationObj({
                  ...paginationObj,
                  PageSize: page,
                  PageNumber: 1,
                });
                dispatch(
                  setComponentState1({
                    entityName: "claimListTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                setPaginationObj({
                  ...paginationObj,
                  PageNumber: paginationObj.PageNumber + 1,
                });
                dispatch(
                  setComponentState1({
                    entityName: "claimListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                setPaginationObj({
                  ...paginationObj,
                  PageNumber: paginationObj.PageNumber - 1,
                });
                dispatch(
                  setComponentState1({
                    entityName: "claimListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          )}
        </>
      </div>
      {showBasicSearch ? (
        <BasicSearch
          basicSearchPayload={basicSearchPayload}
          onApply={handleBasicSearch}
        />
      ) : (
        <></>
      )}
      {showAdvanceSearch && (
        <AdvanceSearch
          setShowAdvanceSearch={setShowAdvanceSearch}
          setAdvanceSearchData={setAdvanceSearchData}
          setIsAdvanceSearchActive={setIsAdvanceSearchActive}
          advanceSearchPayload={advanceSearchPayload}
          setAdvanceSearchPayload={setAdvanceSearchPayload}
          handleReset={handleReset}
        />
      )}
      {showBulkUploadModal && (
        <ClaimBulkImport
          showModal={true}
          setShowModal={setShowBulkUploadModal}
          title={t("pages.claims.claimsTable.bulkImport.title")}
          description={t("pages.claims.claimsTable.bulkImport.description")}
          onDownload={handleDownloadBulkImportTemplate}
          uploadLabel={t("pages.claims.claimsTable.bulkImport.uploadLabel")}
          uploadDescription={t(
            "pages.claims.claimsTable.bulkImport.uploadDescription"
          )}
          accept={t("pages.claims.claimsTable.bulkImport.accept")}
          maxFiles={1}
          onClose={handleClose}
          onBulkCreateSuccess={refreshApiOnBulkClaimCreateSuccess}
          templateDownloadError={templateDownloadError}
          templetDowmloadLoading={templetDowmloadLoading}
          setTemplateDownloadError={setTemplateDownloadError}
        />
      )}
      {showBulkStageAttachmentModal && (
        <BulkStageDocumentAttachment
          showModal={true}
          bulkImportType={bulkImportType}
          setShowModal={setShowBulkStageAttachmentModal}
          title={
            bulkImportType === "stage"
              ? t("pages.claims.claimsTable.bulkStageDocumentImport.title")
              : t("pages.claims.claimsTable.bulkSupportDocumentsImport.title")
          }
          uploadLabel={t("pages.claims.claimsTable.bulkImport.uploadLabel")}
          uploadDescription={t(
            bulkImportType === "stage"
              ? "pages.claims.claimsTable.bulkStageDocumentImport.uploadDescription"
              : "pages.claims.claimsTable.bulkSupportDocumentsImport.uploadDescription"
          )}
          maxFiles={1}
          onClose={() => {
            setShowBulkStageAttachmentModal(false);
            setShowUpdateStatusMessage({
              message: "",
              show: false,
              type: "",
            });
          }}
          templateDownloadError={templateDownloadError}
          setTemplateDownloadError={setTemplateDownloadError}
          templetDowmloadLoading={templetDowmloadLoading}
          onDownload={handleDownloadBulkImportTemplate}
        />
      )}
    </>
  );
}
